import api, { getApiClient } from '~/utils/api';
import type { AxiosRequestConfig } from 'axios';
import { getSectionByPath, getSectionById, generateTitleFromPath, sectionMap } from './musicSectionMapConfig';
import type { SectionMapItem } from './musicSectionMapConfig';

export interface MusicHighlightSegment {
  start: number;
  end: number;
  duration?: number;
  score?: number;
  label?: string;
  confidence?: number;
}

export interface MusicHighlightAnalysis {
  status: 'pending' | 'processing' | 'succeeded' | 'failed';
  version?: string;
  source?: string;
  requested_clip_seconds?: number;
  total_duration_seconds?: number;
  recommended?: MusicHighlightSegment;
  segments?: MusicHighlightSegment[];
  error_code?: string;
  error_message?: string;
  queued_at?: number;
  started_at?: number;
  finished_at?: number;
}

// 定义生成音乐的类型
export interface GenMusic {
  id: string;
  title: string;
  metadata: {
    tags: string;
    prompt: string;
    duration?: number;
    gpt_description_prompt?: string;
    highlight_analysis?: MusicHighlightAnalysis;
    highlight_segments?: MusicHighlightSegment[];
    highlightSegments?: MusicHighlightSegment[];
    sections?: Array<Record<string, unknown>>;
    segments?: Array<Record<string, unknown>>;
    energy_frames?: Array<Record<string, unknown>>;
    energyFrames?: Array<Record<string, unknown>>;
  };
  audio_url: string;
  audio_oss?: string;
  stream_audio_url?: string;
  image_url: string;
  score: number;
  created_at: string;
  finish_time?: number;
}

export interface MusicTask {
  id: string;
  req?: { title?: string; style?: string; moods?: string; tags?: string; type?: string };
  action: string;
  status: number;
  fail_reason: string;
  submit_time: number;
  start_time: number;
  finish_time: number;
  generate_time: number;
  task_time: number;
  progress: string;
  instrumental: boolean;
  music_data: GenMusic[];
  lyrics_data: {
    id: string;
    text: string;
    title: string;
  };
  owner: string;
  cert_time: number;
  cert_number: string;
  created: number;
} 

// 定义音乐榜单类型
export interface MusicRank {
  id: string;
  section_id: string;
  cover_url: string;
  count: number;
  created: number;
  selected_option: string;
  name: string;
  top_songs: any[];
}

// 定义音乐分类类型
export interface MusicCategory {
  id: string;
  image_url: string;
  search_tag: string;
  created: number;
  name: string;
}

// 定义音乐相关的类型
export interface Music {
  id: string;
  section_id?: string;
  title: string;
  video_url?: string;
  audio_url: string;
  image_url: string;
  image_large_url?: string;
  is_video_pending?: boolean;
  is_liked?: boolean;
  is_trashed?: boolean;
  is_public?: boolean;
  is_handle_updated?: boolean;
  handle?: string;
  user_id?: string;
  display_name?: string;
  avatar_image_url?: string;
  model_name?: string;
  major_model_version?: string;
  status?: string;
  play_count?: number;
  upvote_count?: number;
  metadata: {
    prompt: string;
    type?: string;
    duration?: number;
    has_vocal?: boolean;
    is_audio_upload_tos_accepted?: boolean;
    tags: string;
    task?: string;
  };
  created_at: string;
  created: number;
}

// 使用泛型定义响应类型
export interface MusicListResponse<T = Music> {
  data: T[] | {
    list?: T[];
    name?: string;
    total?: number;
    per_page?: number;
  };
  total?: number;
  page?: number;
  limit?: number;
}

export interface MusicQueryParams {
  page?: number;
  size?: number;
  genre?: string;
  search?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

// 歌词生成相关
export interface GenerateLyricsParams {
  prompt?: string;
  topics?: string;
  keyword?: string;
  image?: string;
  title?: string;
  tags?: string;
  moods?: string;
  style?: string;
  type?: string;
  songer_name?: string;  // Add any necessary properties for generating lyrics
}

export interface SearchResultParams {
  // 搜索关键词
  search: string;
  // 分页查询第几页，1 开始
  page: number;
  // 每页条数，最少 10 条，最多 100 条
  size: number;
}

// 本地存储键名
export const LYRIC_HISTORY_KEY = 'lyric_history_cache'
export const RAP_LYRIC_HISTORY_KEY = 'rap_lyric_history_cache'

// 人声分离相关接口
export interface VocalSeparationResponse {
  code: number;
  data: {
    result: {
      task_id: string;
    }
  };
  msg?: string;
}

export interface VocalSeparationParams {
  audio_url?: string;
  file_id?: string;
}

export interface VocalSeparationStatusResponse {
  code: number;
  data: {
    result: {
      task_id: string;
      status: number;
      message: string;
      instrumental_url?: string;
      vocals_url?: string;
    }
  };
  msg?: string;
}

// 专辑封面相关接口
export interface AlbumCoverStyle {
  id: string;
  name: string;
  preview: string;
  font_styles: AlbumCoverFontStyle[];
}

export interface AlbumCoverFontStyle {
  id: string;
  name: string;
  prompt: string;
}

export interface AlbumCoverGenParams {
  prompt?: string;
  style_id?: string;
  font_style_id?: string;
  artist_name?: string;
  album_title?: string;
  bg_file_id?: string;
  ref_file_id?: string;
}

export interface AlbumCoverGenResponse {
  code: number;
  message: string;
  data: {
    task_id: string;
  };
}

export interface AlbumCoverResultResponse {
  code: number;
  message: string;
  data: {
    status: number; // 0=排队 1=生成中 2=完成 3=失败
    image_oss: string;
    error_msg: string;
  };
}

export interface AlbumCoverListItem {
  id: string;
  status: number;
  style_name: string;
  image_oss: string;
  error_msg: string;
  created: number;
}

// MV生成相关接口
export interface MusicVideoPromptSpec {
  overall?: string;
  style?: string;
  narrative?: string;
  character?: string;
  scene?: string;
  extra?: string;
}

export type MusicVideoMode = 'creative' | 'story' | 'template';
export type MusicVideoPublishUse = 'music_video' | 'lyric_video' | 'vertical_promo' | 'canvas_loop' | 'cover_motion';
export type MusicVideoSubtitleMode = 'none' | 'copy' | 'lyrics' | 'srt' | 'auto';

export interface MusicVideoGenParams {
  task_id: string;
  music_id: string;
  request_id?: string;
  images?: string[];
  clip_start?: number;
  clip_end?: number;
  mode?: MusicVideoMode;
  template_id?: string;
  publish_use?: MusicVideoPublishUse;
  size?: '16:9' | '9:16' | '1:1' | '3:4' | '4:3';
  resolution?: '540P' | '720P' | '1080P';
  prompt?: string;
  prompt_spec?: MusicVideoPromptSpec;
  add_subtitle?: boolean;
  subtitle_mode?: MusicVideoSubtitleMode;
  subtitle?: string;
  quote_id?: string;
  accepted_points?: number;
  pricing_version?: string;
  quoted_at?: number;
  quote_request_hash?: string;
  mv_source?: string;
  source_package_id?: string;
  source_music_task_id?: string;
  source_music_id?: string;
  source_reference_image_count?: number;
  source_recommended_clip_start?: number;
  source_recommended_clip_end?: number;
}

export interface MusicVideoGenResponse {
  code: number;
  msg: string;
  data: {
    task_id: string;
    points?: number;
    pricing_version?: string;
  };
}

export interface MusicVideoQuoteParams {
  task_id: string;
  music_id: string;
  clip_start?: number;
  clip_end?: number;
  mode?: MusicVideoMode;
  template_id?: string;
  publish_use?: MusicVideoPublishUse;
  images?: string[];
  add_subtitle?: boolean;
  subtitle_mode?: MusicVideoSubtitleMode;
  resolution?: '540P' | '720P' | '1080P';
  lip_sync?: boolean;
}

export interface MusicVideoResolutionPrice {
  resolution: '540P' | '720P' | '1080P';
  points_per_second: number;
  min_points: number;
  enabled: boolean;
}

export interface MusicVideoBalanceDetail {
  buy_points: number;
  vip_points: number;
  free_points: number;
}

export interface MusicVideoQuoteResponse {
  code: number;
  msg: string;
  data: {
    clip_duration: number;
    resolution: '540P' | '720P' | '1080P';
    lip_sync: boolean;
    points: number;
    min_points: number;
    points_per_second: number;
    balance: number;
    balance_detail?: MusicVideoBalanceDetail;
    can_generate: boolean;
    billing_platform: string;
    min_duration_seconds?: number;
    max_duration_seconds?: number;
    quote_id?: string;
    quote_request_hash?: string;
    quote_ttl_seconds?: number;
    default_resolution: '540P' | '720P' | '1080P';
    enabled_resolutions: Array<'540P' | '720P' | '1080P'>;
    resolution_prices: MusicVideoResolutionPrice[];
    pricing_version: string;
  };
}

export interface MusicVideoResultParams {
  task_id: string;
}

export interface MusicVideoBillingPointDetail {
  points_type: number;
  points: number;
}

export interface MusicVideoBillingSnapshot {
  status?: string;
  billing_platform?: string;
  resolution?: string;
  clip_start?: number;
  clip_end?: number;
  clip_duration?: number;
  points_per_second?: number;
  min_points?: number;
  points?: number;
  points_type?: number;
  details?: MusicVideoBillingPointDetail[];
  pricing_version?: string;
  quote_id?: string;
  accepted_points?: number;
  accepted_pricing_version?: string;
  quoted_at?: number;
}

export interface MusicVideoStoryboardSnapshot {
  index?: number;
  role?: string;
  summary?: string;
}

export interface MusicVideoPromptSnapshot {
  mode?: MusicVideoMode;
  template_id?: string;
  publish_use?: MusicVideoPublishUse;
  clip_position?: string;
  music_segment_summary?: string;
  subtitle_mode?: string;
  uploaded_reference_image_count?: number;
  generated_reference_image_count?: number;
  final_reference_image_count?: number;
  final_reference_image_urls?: string[];
  reference_image_strategy?: string;
  story_input_source?: string;
  story_direction?: string;
  storyboard?: MusicVideoStoryboardSnapshot[];
  user_prompt?: string;
  prompt_spec?: MusicVideoPromptSpec;
}

export interface MusicVideoReq {
  audio: string;
  subtitle?: string;
  subtitle_mode?: MusicVideoSubtitleMode;
  images?: string[];
  gen_images?: string[];
  clip_start?: number;
  clip_end?: number;
  clip_duration?: number;
  mode?: string;
  template_id?: string;
  publish_use?: string;
  size?: string;
  resolution?: string;
  prompt?: string;
  prompt_spec?: MusicVideoPromptSpec;
  add_subtitle?: boolean;
  mv_source?: string;
  source_package_id?: string;
  source_music_task_id?: string;
  source_music_id?: string;
  source_reference_image_count?: number;
  source_recommended_clip_start?: number;
  source_recommended_clip_end?: number;
}

export interface MvInfo {
  id: string;
  user_id: string;
  user_type: number;
  languageid: number;
  req: MusicVideoReq;
  status: number;
  fail_reason?: string;
  start_time?: number;
  finish_time?: number;
  generate_time?: number;
  task_time?: number;
  // 标题
  title: string;
  // 视频url
  video_url?: string;
  // 封面url
  image_url?: string;
  billing?: MusicVideoBillingSnapshot;
  final_prompt_snapshot?: MusicVideoPromptSnapshot;
  refund_status?: string;
  points?: number;
  duration?: number;
  pricing_version?: string;
  // 创建时间，unix毫秒
  created: number;
}

export interface MusicVideoResultResponse {
  code: number;
  msg: string;
  data: MvInfo;
}

export interface MvListParams {
  page: number;
  size: number;
}

export type ContentType = 'music' | 'mv' | 'clip_package';

export interface GrowthEventParams {
  event_id: string;
  content_type: ContentType;
  content_id: string;
  surface: 'clips' | 'clips_templates' | 'share_page';
  event_type: string;
  request_id?: string;
  position?: number;
  anonymous_id?: string;
  user_id?: string;
  session_id: string;
  source_music_id?: string;
  share_token?: string;
  metadata?: Record<string, unknown>;
}

export interface ClipBriefRequest {
  script_or_brief: string;
  brand_profile_id?: string;
  platform: string;
  use_case: string;
  duration: string;
  mood?: string;
  style?: string;
  language?: string;
  brand_or_product_name?: string;
  needs_vertical_video: boolean;
  needs_commercial_license: boolean;
  reference_url?: string;
  visual_assets?: ClipVisualAsset[];
}

export type ClipVisualAssetType = 'product' | 'brand_logo' | 'scene' | 'style' | 'person';
export type ClipVisualAssetSource = 'package_upload' | 'brand_profile' | 'song_cover' | 'generated_reference' | string;

export interface ClipVisualAsset {
  id?: string;
  file_id: string;
  oss_key?: string;
  url?: string;
  type: ClipVisualAssetType;
  label?: string;
  source?: ClipVisualAssetSource;
  use_for_mv: boolean;
  sort: number;
  created_at?: number;
}

export interface ClipBriefAnalysis {
  script_or_brief: string;
  platform: string;
  use_case: string;
  use_case_label: string;
  duration_seconds: number;
  mood_tags: string[];
  style_tags: string[];
  language: string;
  brand_or_product_name?: string;
  needs_vertical_video: boolean;
  needs_commercial_license: boolean;
  reference_url?: string;
  title: string;
  summary: string;
  music_prompt: string;
  caption: string;
  hashtags: string[];
  license_scope: string;
  track_directions: ClipTrackDirection[];
}

export interface ClipTrackDirection {
  id: 'safe_fit' | 'ad_hook' | 'social_punch';
  title: string;
  user_explanation: string;
  prompt_modifier: string;
}

export type ClipHighlightStatus = '' | 'pending' | 'processing' | 'succeeded' | 'failed';

export interface ClipCandidateSnapshot {
  task_id: string;
  music_id: string;
  title: string;
  audio_url: string;
  image_url: string;
  duration_seconds: number;
  tags: string;
  prompt: string;
  highlight_status?: ClipHighlightStatus;
  highlight_label?: string;
  highlight_start?: number;
  highlight_end?: number;
  highlight_error?: string;
}

export interface ClipLicensePayload {
  terms_version: string;
  scope: string;
  proof_url_path: string;
  selected_music_task_id: string;
  selected_music_id: string;
  plan_id: ClipPackagePlanId;
  paid: boolean;
  points: number;
  pricing_version: string;
  created_at: number;
}

export type ClipPackagePlanId = 'free_preview' | 'creator_license' | 'brand_campaign';

export interface ClipPackageOffer {
  plan_id: ClipPackagePlanId;
  title: string;
  points: number;
  commercial_license: boolean;
  rights: string[];
  limitations?: string[];
  pricing_version: string;
}

export interface ClipPackageQuote {
  plan_id: ClipPackagePlanId;
  points: number;
  balance: number;
  can_unlock: boolean;
  pricing_version: string;
  offer: ClipPackageOffer;
}

export interface ClipPackageEntitlement {
  plan_id: ClipPackagePlanId;
  paid: boolean;
  commercial_license: boolean;
  audio_download: boolean;
  public_share: boolean;
  caption_pack: boolean;
  client_proof: boolean;
  brand_archive: boolean;
  rights: string[];
}

export interface ClipPackageBillingPointDetail {
  points_type: number;
  points: number;
}

export interface ClipPackageBilling {
  plan_id: ClipPackagePlanId;
  points: number;
  points_type: number;
  details?: ClipPackageBillingPointDetail[];
  pricing_version: string;
  history_id?: string;
  paid_at?: number;
}

export interface ClipPackage {
  id: string;
  user_id: string;
  brand_profile_id?: string;
  brief: {
    script_or_brief: string;
    platform: string;
    use_case: string;
    duration_seconds: number;
    mood?: string;
    style?: string;
    language?: string;
    brand_or_product_name?: string;
    needs_vertical_video: boolean;
    needs_commercial_license: boolean;
    reference_url?: string;
  };
  analysis: {
    title: string;
    summary: string;
    music_prompt: string;
    caption: string;
    hashtags: string[];
    license_scope: string;
    needs_vertical_video: boolean;
    needs_commercial_license: boolean;
    track_directions: ClipTrackDirection[];
  };
  music_task_id?: string;
  candidates?: ClipCandidateSnapshot[];
  selected_music_id?: string;
  visual_assets?: ClipVisualAsset[];
  license?: ClipLicensePayload;
  selected_plan_id: ClipPackagePlanId;
  entitlement: ClipPackageEntitlement;
  billing?: ClipPackageBilling;
  status: 'draft' | 'generating' | 'music_ready' | 'partial_completed' | 'selected' | 'licensed' | 'published' | 'failed' | string;
  failure_reason?: string;
  is_public: boolean;
  published_at?: number;
  created: number;
  updated: number;
}

export type ClipPackageTaskFilter = 'all' | 'priority' | 'in_progress' | 'needs_action' | 'failed' | 'published';
export type ClipPackageTaskStatus =
  | 'draft'
  | 'generating'
  | 'music_ready'
  | 'partial_completed'
  | 'selected'
  | 'licensed'
  | 'published'
  | 'failed'
  | string;
export type ClipPackageTaskAction =
  | 'continue'
  | 'view_progress'
  | 'choose_track'
  | 'use_available'
  | 'license'
  | 'publish'
  | 'view_share'
  | 'retry'
  | string;

export interface ClipPackageTaskListItem {
  package_id: string;
  batch_id?: string;
  batch_source_package_id?: string;
  batch_direction_id?: string;
  batch_direction_title?: string;
  batch_created?: number;
  title: string;
  brief_summary: string;
  platform?: string;
  use_case?: string;
  duration_seconds?: number;
  status: ClipPackageTaskStatus;
  workflow_stage: string;
  status_label: string;
  next_action: ClipPackageTaskAction;
  next_action_url: string;
  has_music_task: boolean;
  has_candidates: boolean;
  has_selected_track: boolean;
  has_license: boolean;
  visual_asset_count?: number;
  mv_reference_ready?: boolean;
  mv_reference_source?: string;
  is_public: boolean;
  failure_reason?: string;
  highlight_status?: ClipHighlightStatus;
  highlight_label?: string;
  highlight_start?: number;
  highlight_end?: number;
  highlight_error?: string;
  created_at: number;
  updated_at: number;
}

export interface ClipPackageListParams {
  status?: ClipPackageTaskFilter;
  cursor?: number;
  cursor_created?: number;
  cursor_id?: string;
  limit?: number;
  q?: string;
}

export interface ClipPackageListResponse {
  code: number;
  msg?: string;
  data: {
    items: ClipPackageTaskListItem[];
    next_cursor?: number;
    next_cursor_created?: number;
    next_cursor_id?: string;
    batch_context_available?: boolean;
  };
}

export interface ClipMusicRequest {
  prompt: string;
  title?: string;
  tags?: string;
  gpt_description_prompt?: string;
  type: string;
  task_time: string;
  default_publish_enabled: boolean;
  publish_terms_version: string;
}

export interface ClipDirectionMusicRequest {
  direction_id: ClipTrackDirection['id'];
  direction_title: string;
  user_explanation: string;
  request: ClipMusicRequest;
}

export interface ClipCreateResponse {
  code: number;
  msg?: string;
  data: {
    package: ClipPackage;
    analysis: ClipBriefAnalysis;
    music_request: ClipMusicRequest;
    music_requests: ClipDirectionMusicRequest[];
    offers: ClipPackageOffer[];
  };
}

export interface ClipPackageResponse {
  code: number;
  msg?: string;
  data: ClipPackage;
}

export interface ClipLicenseResponse {
  code: number;
  msg?: string;
  data: ClipLicensePayload;
}

export interface ClipPackageQuoteResponse {
  code: number;
  msg?: string;
  data: ClipPackageQuote;
}

export interface ClipPackageUnlockResponse {
  code: number;
  msg?: string;
  data: {
    package: ClipPackage;
    quote: ClipPackageQuote;
  };
}

export interface ClipPublicSEO {
  title: string;
  description: string;
  keywords: string[];
  image_url?: string;
}

export interface ClipPublicRemixRequest extends ClipBriefRequest {
  source_package_id?: string;
}

export interface ClipPublicCTA {
  id: string;
  label: string;
  href: string;
  event_type: string;
}

export interface ClipPublicGrowthPayload {
  package_id: string;
  canonical_path: string;
  template_id: string;
  seo: ClipPublicSEO;
  remix_request: ClipPublicRemixRequest;
  ctas: ClipPublicCTA[];
  trackable_events: string[];
}

export interface ClipGrowthTemplate {
  id: string;
  title: string;
  description: string;
  category: string;
  remix_request: ClipPublicRemixRequest;
  cta: ClipPublicCTA;
}

export interface ClipPublicGrowthResponse {
  code: number;
  msg?: string;
  data: ClipPublicGrowthPayload;
}

export interface ClipGrowthTemplatesResponse {
  code: number;
  msg?: string;
  data: ClipGrowthTemplate[];
}

export interface ClipBrandProfile {
  id: string;
  user_id: string;
  name: string;
  product_description?: string;
  target_audience?: string;
  brand_voice?: string;
  visual_style?: string;
  preferred_platforms?: string[];
  restricted_words?: string[];
  brand_colors?: string[];
  logo_url?: string;
  reference_url?: string;
  created: number;
  updated: number;
}

export interface ClipBrandProfileRequest {
  profile_id?: string;
  name: string;
  product_description?: string;
  target_audience?: string;
  brand_voice?: string;
  visual_style?: string;
  preferred_platforms?: string[];
  restricted_words?: string[];
  brand_colors?: string[];
  logo_url?: string;
  reference_url?: string;
}

export interface ClipBrandProfileResponse {
  code: number;
  msg?: string;
  data: ClipBrandProfile;
}

export interface ClipBrandProfilesResponse {
  code: number;
  msg?: string;
  data: ClipBrandProfile[];
}

export interface ClipBatchRequest {
  source_package_id?: string;
  brand_profile_id?: string;
  base_brief: ClipBriefRequest;
  target_platforms: string[];
  variants_per_platform: number;
}

export interface ClipBatchVariant {
  id: string;
  package_id?: string;
  platform: string;
  direction_id: string;
  direction_title: string;
  status: 'package_created' | 'failed' | string;
  brief?: ClipPackage['brief'];
  music_prompt?: string;
  error?: string;
}

export interface ClipBatch {
  id: string;
  user_id?: string;
  source_package_id?: string;
  brand_profile_id?: string;
  request?: {
    target_platforms: string[];
    variants_per_platform: number;
  };
  variants: ClipBatchVariant[];
  total_variants: number;
  package_ready_variants: number;
  failed_variants: number;
  status: 'package_ready' | string;
  created?: number;
  updated?: number;
}

export interface ClipBatchResponse {
  code: number;
  msg?: string;
  data: {
    batch: ClipBatch;
  } | ClipBatch;
}

export interface ClipBatchesResponse {
  code: number;
  msg?: string;
  data: ClipBatch[];
}

export interface ClipPerformanceEventCounts {
  public_views: number;
  audio_plays: number;
  make_yours_clicks: number;
  license_views: number;
  batch_creates: number;
  batch_variant_opens: number;
}

export interface ClipPerformanceRatios {
  audio_play_rate: number;
  remix_rate: number;
  license_rate: number;
}

export interface ClipPerformanceSuggestion {
  id: string;
  title: string;
  reason: string;
  remix_request: ClipPublicRemixRequest;
}

export interface ClipPerformancePayload {
  package_id: string;
  batch_id?: string;
  start: number;
  end: number;
  total_events: number;
  counts: ClipPerformanceEventCounts;
  ratios: ClipPerformanceRatios;
  suggestions: ClipPerformanceSuggestion[];
  by_event_type?: Record<string, number>;
}

export interface ClipPerformanceResponse {
  code: number;
  msg?: string;
  data: ClipPerformancePayload;
}

export interface ClipOpsProviderCapability {
  provider: string;
  label: string;
  enabled: boolean;
  candidate?: boolean;
  supports_vertical?: boolean;
  supports_commercial?: boolean;
  notes?: string[];
}

export interface ClipOpsProviderPolicy {
  primary_provider: string;
  fallback_provider?: string;
  auto_provider_switch: boolean;
  providers: ClipOpsProviderCapability[];
}

export interface ClipOpsLimits {
  max_batch_variants: number;
  max_daily_packages_per_user: number;
  max_daily_batch_variants: number;
  max_daily_clip_events: number;
  max_estimated_provider_cost_rmb: number;
  require_paid_plan_for_commercial: boolean;
}

export interface ClipOpsSignals {
  start: number;
  end: number;
  packages_created: number;
  batches_created: number;
  batch_variants_created: number;
  failed_batch_variants: number;
  clip_events: number;
  public_views: number;
  make_yours_clicks: number;
  license_views: number;
}

export interface ClipOpsGate {
  id: string;
  label: string;
  status: 'pass' | 'warn' | 'block' | string;
  detail: string;
}

export interface ClipOpsReadinessPayload {
  status: 'ready' | 'monitor' | 'blocked' | string;
  provider: ClipOpsProviderPolicy;
  limits: ClipOpsLimits;
  signals: ClipOpsSignals;
  gates: ClipOpsGate[];
  warnings: string[];
  blockers: string[];
  next_actions: string[];
}

export interface ClipOpsReadinessResponse {
  code: number;
  msg?: string;
  data: ClipOpsReadinessPayload;
}

export interface AttachClipPackageMusicParams {
  package_id: string;
  music_task_id: string;
  candidates: ClipCandidateSnapshot[];
}

export interface UpdateClipPackageVisualAssetsParams {
  package_id: string;
  visual_assets: ClipVisualAsset[];
}

export interface AnalyzeMusicHighlightParams {
  package_id: string;
  music_task_id: string;
  music_id: string;
  requested_clip_seconds?: number;
  force?: boolean;
}

export interface AnalyzeMusicHighlightResponse {
  code: number;
  msg?: string;
  data: {
    queued: boolean;
    task_id: string;
    status: ClipHighlightStatus;
  };
}

export interface SelectClipPackageTrackParams {
  package_id: string;
  selected_music_task_id: string;
  selected_music_id: string;
  commercial: boolean;
}

// 导出路径常量
export const API_PATHS_MUSIC = {
  VOCAL_SEPARATION: {
    STATUS: "music/v1/audio/status",
    STATUS_WITHOUT_AUTH: "/music/public/v1/audio/status",
    SPLIT: "/music/v1/audio/split",
    SPLIT_WITHOUT_AUTH: "/music/public/v1/audio/split"
  },
  UPLOAD: {
    FILE: "/music/v1/upload",
    FILE_WITHOUT_AUTH: "/music/public/v1/upload"
  },
  LYRICS: {
    GENERATE: "/music/v1/lyrics",
    GENERATE_WITHOUT_AUTH: "/music/public/v1/lyrics"
  },
  TASK: {
    RESULT: "/music/v1/result",
    RESULT_WITHOUT_AUTH: "/music/public/v1/result"
  },
  MV: {
    GEN: "/music/v1/mv/gen",
    UPLOAD: "/music/v1/mv/upload",
    QUOTE: "/music/v1/mv/quote",
    RESULT: "/music/v1/mv/result"
  },
  CLIPS: {
    CREATE: "/music/v1/clips/create",
    LIST: "/music/v1/clips/list",
    DETAIL: "/music/v1/clips/detail",
    ATTACH_MUSIC: "/music/v1/clips/attach-music",
    VISUAL_ASSETS: "/music/v1/clips/visual-assets",
    HIGHLIGHT_ANALYZE: "/music/v1/music/highlight/analyze",
    SELECT_TRACK: "/music/v1/clips/select-track",
    PUBLISH: "/music/v1/clips/publish",
    QUOTE: "/music/v1/clips/quote",
    UNLOCK: "/music/v1/clips/unlock",
    PUBLIC_DETAIL: "music/public/v1/clips/detail",
    PUBLIC_LICENSE: "music/public/v1/clips/license",
    GROWTH: "music/public/v1/clips/growth",
    TEMPLATES: "music/public/v1/clips/templates",
    BRAND_CREATE: "/music/v1/clips/brands/create",
    BRAND_LIST: "/music/v1/clips/brands/list",
    BRAND_UPDATE: "/music/v1/clips/brands/update",
    BRAND_DETAIL: "/music/v1/clips/brands/detail",
    BATCH_CREATE: "/music/v1/clips/batches/create",
    BATCH_DETAIL: "/music/v1/clips/batches/detail",
    BATCH_LIST: "/music/v1/clips/batches/list",
    PERFORMANCE: "/music/v1/clips/performance",
    OPS_READINESS: "music/public/local/clips/ops/readiness",
  },
  ALBUM_COVER: {
    STYLES: "music/public/v1/albumcoverstyles",
    GEN: "music/v1/albumcovergen",
    RESULT: "music/v1/albumcoverresult",
    DELETE: "music/v1/albumcoverdelete",
    LIST: "music/v1/albumcoverlist",
    APPLY: "music/v1/albumcoverapply",
    DOWNLOAD: "music/v1/albumcoverdownload",
    SHARED_DOWNLOAD: "music/public/v1/albumcoverdownload"
  }
} as const;

// 音乐服务API
export const musicService = {
  // 获取订阅配置列表
  getSubscriptionConfig: (params?: any, config?: AxiosRequestConfig) => {
    return api.post<any>("music/v1/products", params, config);
  },
  // 获取积分历史记录
  getScoreHistory: (params?: any, config?: AxiosRequestConfig) => {
    return api.post<any>("music/v1/pointshis", params, config);
  },
  // 获取分享音乐信息
  getPublicShareMusic: (params: { token: string }, config?: AxiosRequestConfig) => {
    return api.post<any>("music/public/share", params, config);
  },

  // 获取用户积分
  getUserAccount: (params?: any) => {
    return api.post<any>("music/v1/status", params);
  },
  // 获取音乐榜单列表
  getFeaturedMusic: (params?: MusicQueryParams, config?: AxiosRequestConfig) => {
    return api.post<MusicListResponse<MusicRank>>("music/v1/trending", params, config);
  },

  // 获取更多榜单音乐数据
  getMoreChartMusic: (
    section_id?: string,
    selected_option?: string,
    params?: MusicQueryParams,
    config?: AxiosRequestConfig
  ) => {
    return api.post<MusicListResponse<Music>>("music/v1/trending/more", {
      section_id,
      selected_option,
      ...params,
    }, config);
  },

  // 获取音乐分类列表
  getCategoryList: (params?: MusicQueryParams, config?: AxiosRequestConfig) => {
    return api.post<MusicListResponse<MusicCategory>>(
      "music/v1/category",
      params,
      config
    );
  },

  // 获取更多分类音乐数据
  getMoreCategoryMusic: (search_tag: string, params?: MusicQueryParams, config?: AxiosRequestConfig) => {
    return api.post<MusicListResponse<Music>>("music/v1/category/more", {
      search_tag,
      ...params,
    }, config);
  },
  // 获取风格列表
  getStyleList: (params?: MusicQueryParams) => {
    return api.post<MusicListResponse<MusicCategory>>(
      "music/v1/styles",
      params
    );
  },
  // 获取Mood列表
  getMoodList: () => {
    return api.post<MusicListResponse<MusicCategory>>("music/v1/styles", {
      type: 1,
    });
  },
  // 随机生成prompt
  setRandomPrompt: (params?: any) => {
    return api.post<any>("music/v1/prompts", params);
  },
  // 生成歌词
  generateLyrics: (path: string, params: GenerateLyricsParams) => {
    return api.post<any>(path, params);
  },

  //轮询歌词/歌曲生成结果, 默认鉴权
  getMusicTaskResult: (
    params?: any,
    path: string = API_PATHS_MUSIC.TASK.RESULT,
    config?: AxiosRequestConfig
  ) => {
    return config ? api.post<any>(path, params, config) : api.post<any>(path, params);
  },
  // 创建新音乐
  createMusic: (musicData: Partial<Music>) => {
    return api.post<Music>("/music/v1/music", musicData);
  },

  // 我创作的音乐列表
  getMyMusicList: (params?: any, config?: AxiosRequestConfig) => {
    return api.post<MusicListResponse<Music>>("music/v1/record/page", params, config);
  },

  // 获取歌词生成历史
  getLyricHistory: async (params?: any, config?: AxiosRequestConfig) => {
    // 获取远程数据
    return await api.post<MusicListResponse<Music>>("music/v1/record/page", {
      action: "LYRICS",
      ...params,
    }, config);
  },

  // 上传文件
  uploadFile: (
    path: string,
    file: File,
    onProgress?: (progress: number) => void
  ) => {
    return api.uploadFile<any>(path, file, onProgress);
  },

  // 保存歌词生成记录到本地存储
  saveLyricHistory: (task: MusicTask, type: string = LYRIC_HISTORY_KEY) => {
    if (typeof window === "undefined") return;172.161721

    try {
      // 获取现有记录
      const existingHistory = localStorage.getItem(type);
      let history: MusicTask[] = [];

      if (existingHistory) {
        history = JSON.parse(existingHistory);
      }

      // 将新记录添加到开头
      history.unshift(task);

      // 限制存储最近的50条记录
      if (history.length > 50) {
        history = history.slice(0, 50);
      }

      // 保存回本地存储
      localStorage.setItem(type, JSON.stringify(history));
    } catch (error) {
      console.error("保存歌词历史记录失败:", error);
    }
  },

  // 从本地存储获取歌词生成记录
  getLocalLyricHistory: (type: string = LYRIC_HISTORY_KEY): MusicTask[] => {
    if (typeof window === "undefined") return [];

    try {
      const history = localStorage.getItem(type);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error("获取本地歌词历史记录失败:", error);
      return [];
    }
  },

  // 清除本地歌词生成记录
  clearLocalLyricHistory: (type: string = LYRIC_HISTORY_KEY) => {
    if (typeof window === "undefined") return;

    try {
      localStorage.removeItem(type);
    } catch (error) {
      console.error("清除本地歌词历史记录失败:", error);
    }
  },

  // 删除音乐记录或任务
  deleteRecord: (params: { category: number; ids: string[] }) => {
    return api.post<any>("music/v1/record/rm", params);
  },
  // 生成嘻哈名字
  generateRapName: (params?: any, config?: AxiosRequestConfig) => {
    return api.post<any>("music/v1/rapname/gen", params, { ...config, timeout: 60000 });
  },

  // 获取搜索热词
  getSearchHotWords: () => {
    return api.post<any>("music/v1/hotword");
  },

  // 获取搜索结果
  getSearchResult: (params: SearchResultParams) => {
    return api.post<MusicListResponse<Music>>("music/v1/search", params);
  },

  // 修改歌曲名称
  updateMusicTitle: (params?: any) => {
    return api.post<any>("music/v1/changetitle", params);
  },

  getPublicMusicDetail: (id: string, config?: AxiosRequestConfig) => {
    return api.post<any>("music/v1/get", { id }, config);
  },

  // 开始人声分离
  startVocalSeparation: (path: string, params: VocalSeparationParams) => {
    return api.post<VocalSeparationResponse>(path, params);
  },

  // 获取人声分离状态
  getVocalSeparationStatus: (path: string, params: { task_id: string }) => {
    return api.post<VocalSeparationStatusResponse>(path, params);
  },

  // 生成MV
  generateMusicVideo: (params: MusicVideoGenParams) => {
    return api.post<MusicVideoGenResponse>(API_PATHS_MUSIC.MV.GEN, params);
  },

  quoteMusicVideo: (params: MusicVideoQuoteParams, config?: AxiosRequestConfig) => {
    return api.post<MusicVideoQuoteResponse>(API_PATHS_MUSIC.MV.QUOTE, params, config);
  },

  // 获取MV生成结果
  getMusicVideoResult: (params: MusicVideoResultParams) => {
    return api.post<MusicVideoResultResponse>(
      API_PATHS_MUSIC.MV.RESULT,
      params
    );
  },

  // 我创作的MV列表
  getMvList: (params: MvListParams, config?: AxiosRequestConfig) => {
    return api.post<MusicListResponse<MvInfo>>("music/v1/mv/page", params, config);
  },
  trackGrowthEvent: (params: GrowthEventParams) => {
    return api.post<any>("music/public/v1/growth/events", params);
  },
  createClipPackage: (params: ClipBriefRequest) => {
    return api.post<ClipCreateResponse>(API_PATHS_MUSIC.CLIPS.CREATE, params);
  },
  listClipPackages: (params: ClipPackageListParams = {}, config?: AxiosRequestConfig) => {
    return api.post<ClipPackageListResponse>(API_PATHS_MUSIC.CLIPS.LIST, params, config);
  },
  getClipPackage: (params: { package_id: string }, config?: AxiosRequestConfig) => {
    return api.post<ClipPackageResponse>(API_PATHS_MUSIC.CLIPS.DETAIL, params, config);
  },
  attachClipPackageMusic: (params: AttachClipPackageMusicParams) => {
    return api.post<ClipPackageResponse>(API_PATHS_MUSIC.CLIPS.ATTACH_MUSIC, params);
  },
  updateClipPackageVisualAssets: (params: UpdateClipPackageVisualAssetsParams) => {
    return api.post<ClipPackageResponse>(API_PATHS_MUSIC.CLIPS.VISUAL_ASSETS, params);
  },
  analyzeMusicHighlight: (params: AnalyzeMusicHighlightParams, config?: AxiosRequestConfig) => {
    return api.post<AnalyzeMusicHighlightResponse>(API_PATHS_MUSIC.CLIPS.HIGHLIGHT_ANALYZE, params, config);
  },
  selectClipPackageTrack: (params: SelectClipPackageTrackParams) => {
    return api.post<ClipPackageResponse>(API_PATHS_MUSIC.CLIPS.SELECT_TRACK, params);
  },
  publishClipPackage: (params: { package_id: string }) => {
    return api.post<ClipPackageResponse>(API_PATHS_MUSIC.CLIPS.PUBLISH, params);
  },
  quoteClipPackage: (params: { package_id: string; plan_id: ClipPackagePlanId }) => {
    return api.post<ClipPackageQuoteResponse>(API_PATHS_MUSIC.CLIPS.QUOTE, params);
  },
  unlockClipPackage: (params: { package_id: string; plan_id: ClipPackagePlanId; accepted_points: number; pricing_version: string }) => {
    return api.post<ClipPackageUnlockResponse>(API_PATHS_MUSIC.CLIPS.UNLOCK, params);
  },
  getPublicClipPackage: (params: { package_id: string }) => {
    return api.post<ClipPackageResponse>(API_PATHS_MUSIC.CLIPS.PUBLIC_DETAIL, params);
  },
  getPublicClipPackageLicense: (params: { package_id: string }) => {
    return api.post<ClipLicenseResponse>(API_PATHS_MUSIC.CLIPS.PUBLIC_LICENSE, params);
  },
  getPublicClipPackageGrowth: (params: { package_id: string }) => {
    return api.post<ClipPublicGrowthResponse>(API_PATHS_MUSIC.CLIPS.GROWTH, params);
  },
  listPublicClipGrowthTemplates: () => {
    return api.post<ClipGrowthTemplatesResponse>(API_PATHS_MUSIC.CLIPS.TEMPLATES, {});
  },
  createClipBrandProfile: (params: ClipBrandProfileRequest) => {
    return api.post<ClipBrandProfileResponse>(API_PATHS_MUSIC.CLIPS.BRAND_CREATE, params);
  },
  listClipBrandProfiles: (config?: AxiosRequestConfig) => {
    return api.post<ClipBrandProfilesResponse>(API_PATHS_MUSIC.CLIPS.BRAND_LIST, {}, config);
  },
  updateClipBrandProfile: (params: ClipBrandProfileRequest & { profile_id: string }) => {
    return api.post<ClipBrandProfileResponse>(API_PATHS_MUSIC.CLIPS.BRAND_UPDATE, params);
  },
  getClipBrandProfile: (params: { profile_id: string }) => {
    return api.post<ClipBrandProfileResponse>(API_PATHS_MUSIC.CLIPS.BRAND_DETAIL, params);
  },
  createClipBatch: (params: ClipBatchRequest) => {
    return api.post<ClipBatchResponse>(API_PATHS_MUSIC.CLIPS.BATCH_CREATE, params);
  },
  getClipBatch: (params: { batch_id: string }) => {
    return api.post<ClipBatchResponse>(API_PATHS_MUSIC.CLIPS.BATCH_DETAIL, params);
  },
  listClipBatches: (config?: AxiosRequestConfig) => {
    return api.post<ClipBatchesResponse>(API_PATHS_MUSIC.CLIPS.BATCH_LIST, {}, config);
  },
  getClipPackagePerformance: (params: { package_id: string; batch_id?: string; start?: number; end?: number; limit?: number }, config?: AxiosRequestConfig) => {
    return api.post<ClipPerformanceResponse>(API_PATHS_MUSIC.CLIPS.PERFORMANCE, params, config);
  },
  getClipOpsReadiness: (params: { start?: number; end?: number; limit?: number } = {}) => {
    return api.post<ClipOpsReadinessResponse>(API_PATHS_MUSIC.CLIPS.OPS_READINESS, params);
  },
  // 生成分享链接
  generateShareLink: (params: { id: string }) => {
    return api.post<any>("/music/v1/genshare", params);
  },

  // 获取专辑封面风格列表（公开接口）
  getAlbumCoverStyles: () => {
    return api.post<any>(API_PATHS_MUSIC.ALBUM_COVER.STYLES, {});
  },

  // 创建专辑封面生成任务
  createAlbumCoverTask: (params: AlbumCoverGenParams) => {
    return api.post<AlbumCoverGenResponse>(API_PATHS_MUSIC.ALBUM_COVER.GEN, params);
  },

  // 查询专辑封面生成结果
  getAlbumCoverResult: (params: { task_id: string }) => {
    return api.post<AlbumCoverResultResponse>(API_PATHS_MUSIC.ALBUM_COVER.RESULT, params);
  },

  // 删除专辑封面记录
  deleteAlbumCover: (params: { task_id: string }) => {
    return api.post<any>(API_PATHS_MUSIC.ALBUM_COVER.DELETE, params);
  },

  // 获取专辑封面生成历史
  getAlbumCoverList: (params: { page: number; page_size: number }, config?: AxiosRequestConfig) => {
    return api.post<any>(API_PATHS_MUSIC.ALBUM_COVER.LIST, params, config);
  },

  // Read the owned original through our API; the public image CDN has no CORS headers.
  downloadAlbumCover: async (taskId: string) => {
    const blob = await api.post<Blob>(API_PATHS_MUSIC.ALBUM_COVER.DOWNLOAD, { task_id: taskId }, { responseType: 'blob', timeout: 30000 });
    return new Response(blob);
  },

  // Share links already contain the public image. This request never sends a login token or cookies.
  downloadSharedAlbumCover: (taskId: string, imageUrl: string, signal?: AbortSignal) => {
    const base = (getApiClient().defaults.baseURL || window.location.origin).replace(/\/$/, '');
    return fetch(`${base}/${API_PATHS_MUSIC.ALBUM_COVER.SHARED_DOWNLOAD}`, {
      method: 'POST', credentials: 'omit', signal,
      headers: { 'Content-Type': 'application/json', 'X-PLATFORM': 'web' },
      body: JSON.stringify({ task_id: taskId, image_url: imageUrl }),
    });
  },

  // 将生成的封面应用到歌曲
  applyAlbumCover: (params: { task_id: string; music_id: string }) => {
    return api.post<{ code: number; data: { image_url: string } }>(API_PATHS_MUSIC.ALBUM_COVER.APPLY, params);
  },
};

export default musicService; 
